<template>
  <div>
    <MainLoading v-if="isLoading" />
    <MainError
      v-else-if="hasError"
      title="Não foi possivel carregar os proventos provisionados"
      description="Aguarde alguns minutos e tente novamente."
      @on-retry="getProvisionedProfitData"
    />
    <div v-else class="provisioned-profits">
      <Heading :level="2" class="provisioned-profits__title">
        Provisionados
      </Heading>
      <HeadingDisplay
        title=""
        size="extra-small"
        head="Proventos a receber"
        class="provisioned-profits__heading-display"
      >
        <template #title-component>
          <Money>{{ profitData?.expectedAmount.formatted }}</Money>
        </template>
      </HeadingDisplay>

      <DataTable
        show-divider
        :status="tableStatus"
        :columns="columns"
        :data="profitData?.stocks"
        :has-interaction="false"
      >
        <template #error>
          <HelperViewStatus
            status="alert"
            class="provisioned-profits__helper"
            button-label="Tentar novamente"
            title="Desculpe, não conseguimos carregar os proventos a receber"
            description="Aguarde um momento e tente novamente"
            @click="getProvisionedProfitData"
          />
        </template>
        <template #empty>
          <Heading :level="6" class="provisioned-profits__empty-title">
            Você não tem proventos provisionados no momento
          </Heading>
        </template>
      </DataTable>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Heading,
  HeadingDisplay,
  DataTable,
  HelperViewStatus
} from '@warrenbrasil/nebraska-web';
import { MainError } from '@/modules/trade/components/MainError';
import { MainLoading } from '@/modules/trade/components/MainLoading';
import { Money } from '@/modules/common/components/MoneyV2';
import { computed, onMounted, ref } from 'vue';
import {
  getProvisionedProfits,
  ProvisionedProfitResponse
} from '@/modules/wealth/services/portfolio';
import { columns } from './columns';

interface Props {
  portfolioId: string;
}
const props = defineProps<Props>();

const isLoading = ref(false);
const hasError = ref(false);
const profitData = ref<ProvisionedProfitResponse | null>(null);

const getProvisionedProfitData = async () => {
  isLoading.value = true;
  hasError.value = false;

  try {
    profitData.value = await getProvisionedProfits(props.portfolioId);
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const tableStatus = computed(() => {
  if (hasError.value) return 'error';
  if (isLoading.value) return 'loading';

  return 'default';
});

onMounted(getProvisionedProfitData);
</script>

<style lang="less" scoped>
.provisioned-profits {
  margin-top: @size-spacing-x1200;

  &__title {
    margin-bottom: @size-spacing-x400;
  }

  &__heading-display {
    padding-bottom: @size-spacing-x500;
    border-bottom: @size-border-x400 @divider-primary solid;
  }

  &__helper {
    margin: @size-spacing-x500 auto;
    justify-content: center;
  }

  &__empty-title {
    margin-top: @size-spacing-x500;
    text-align: center;
    font-weight: bold;
  }
}
</style>
